import React, { useCallback, useState } from "react";
import cx from "classnames";
import { Spin, message } from "antd";
import corporateLogo from "assets/images/spikex-card/logo2.png";
import mastercardMark from "assets/images/spikex-card/mastercard-mark.png";
import iconAttachmentRequired from "assets/images/spikex-card/icon-attachment-required.svg";
import iconBank from "assets/images/spikex-card/icon-bank.svg";
import iconCopy from "assets/images/spikex-card/icon-copy.svg";
import iconRule from "assets/images/spikex-card/icon-rule.svg";
import { copyToClipboard } from "@/utils/method";
import { resolveHyperpayErrorMessage } from "@/utils/hyperpayErrors";
import {
  formatCardBalanceWithCurrency,
  formatCardLast4,
  formatGroupedCardNumber,
  formatMaskedCardNumber,
} from "@/utils/spikexCard";
import styles from "./index.module.less";
import CardRulesModal from "./CardRulesModal";

function ChevronRightIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
      <path
        d="M4.05 2.55 7.95 6 4.05 9.45"
        stroke="#8E90A2"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function EyeIcon({ revealed }) {
  if (revealed) {
    return (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
        <path
          d="M3 10s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5Z"
          stroke="#999999"
          strokeWidth="1.2"
        />
        <circle cx="10" cy="10" r="2.2" stroke="#999999" strokeWidth="1.2" />
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
      <path
        d="M3 10s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5Z"
        stroke="#999999"
        strokeWidth="1.2"
      />
      <circle cx="10" cy="10" r="2.2" stroke="#999999" strokeWidth="1.2" />
      <path d="m4 4 12 12" stroke="#999999" strokeWidth="1.2" />
    </svg>
  );
}

function ManagedCardVisual({ t, frozen, cardNumber }) {
  const last4 = formatCardLast4(cardNumber);

  return (
    <section
      className={`${styles.cardVisual} ${styles.managedCardVisual} ${frozen ? styles.managedCardVisualFrozen : ""}`}
      aria-label={t("spikexCard.title")}
    >
      <div className={styles.cardBg} aria-hidden />
      <img className={styles.mastercardMark} src={mastercardMark} alt="" />
      <img className={styles.corporateLogo} src={corporateLogo} alt="" />
      <div className={styles.memberBlock}>
        <span className={styles.memberLabel}>{t("spikexCard.member")}</span>
        <span className={styles.memberPan}>**** **** **** {last4}</span>
      </div>
    </section>
  );
}

function CopyButton({ t, text }) {
  const handleCopy = useCallback(() => {
    if (!text) return;
    copyToClipboard(text);
    message.success(t("spikexCard.copySuccess"));
  }, [t, text]);

  return (
    <button type="button" className={styles.cardInfoCopyBtn} onClick={handleCopy} aria-label={t("spikexCard.copy")}>
      <img className={styles.cardInfoCopyIcon} src={iconCopy} alt="" aria-hidden />
    </button>
  );
}

function BalancePanel({ t, balance, cardCurrency, balanceLoading, onOpenCardInfo, onOpenRules, onDeposit }) {
  return (
    <section className={styles.balanceCard}>
      <div className={styles.balanceCardActions}>
        <button
          type="button"
          className={styles.balanceCardActionBtn}
          onClick={onOpenRules}
          aria-label={t("spikexCard.cardRules")}
        >
          <img src={iconRule} alt="" aria-hidden />
        </button>
        <button
          type="button"
          className={styles.balanceCardActionBtn}
          onClick={onOpenCardInfo}
          aria-label={t("spikexCard.cardInfoTitle")}
        >
          <img src={iconBank} alt="" aria-hidden />
        </button>
      </div>
      <div className={styles.balanceCardBody}>
        <div className={styles.balanceAmountBlock}>
          <span className={styles.balanceLabel}>{t("spikexCard.availableBalance")}</span>
          <div className={styles.balanceValue}>
            {balanceLoading ? (
              <Spin size="small" />
            ) : (
              formatCardBalanceWithCurrency(balance, cardCurrency)
            )}
          </div>
        </div>
        <button type="button" className={styles.balanceDepositBtn} onClick={onDeposit}>
          {t("spikexCard.topUp")}
        </button>
      </div>
    </section>
  );
}

function RecentTransactionsPanel({ t, transactions, loading, onViewAll }) {
  return (
    <section className={styles.recentTxCard}>
      <div className={styles.recentTxHeader}>
        <h3 className={styles.recentTxTitle}>{t("spikexCard.recentTransactions")}</h3>
        <button type="button" className={styles.recentTxViewAllBtn} onClick={onViewAll}>
          <span>{t("spikexCard.viewAll")}</span>
          <ChevronRightIcon />
        </button>
      </div>
      <div className={styles.recentTxList}>
        {loading ? (
          <div className={styles.recentTxLoading}>
            <Spin size="small" />
          </div>
        ) : transactions.length === 0 ? (
          <div className={styles.recentTxEmpty}>{t("spikexCard.transactionsEmpty")}</div>
        ) : (
          transactions.map((item, index) => (
            <React.Fragment key={item.id}>
              {index > 0 ? <div className={styles.recentTxDivider} aria-hidden /> : null}
              <div className={styles.recentTxItem}>
                <div className={styles.recentTxMain}>
                  <div className={styles.recentTxName}>
                    {item.titleKey ? t(item.titleKey) : item.title || "—"}
                  </div>
                  <div className={styles.recentTxMeta}>
                    {t("spikexCard.transactionSpendAt", { datetime: item.spendAt })}
                  </div>
                </div>
                <div className={styles.recentTxSide}>
                  <div
                    className={`${styles.recentTxAmount} ${
                      String(item.amountSigned || item.amount || "").startsWith("+")
                        ? styles.recentTxAmountPositive
                        : ""
                    }`}
                  >
                    {item.amountSigned ||
                      (String(item.amount).startsWith("-")
                        ? `-$${String(item.amount).slice(1)}`
                        : String(item.amount).startsWith("+")
                          ? `+$${String(item.amount).slice(1)}`
                          : `$${item.amount}`)}
                  </div>
                  <div className={styles.recentTxStatus}>{t(item.statusKey)}</div>
                </div>
              </div>
            </React.Fragment>
          ))
        )}
      </div>
    </section>
  );
}

function CardInfoPanel({
  t,
  cardBalance,
  cardSensitive,
  sensitiveRevealed,
  sensitiveLoading,
  onToggleReveal,
  onBack,
  spikexPc = false,
}) {
  const maskedNumber = formatMaskedCardNumber(cardBalance?.cardNumber);
  const fullNumber = sensitiveRevealed ? formatGroupedCardNumber(cardSensitive?.cardNumber) : maskedNumber;
  const expire = sensitiveRevealed ? cardSensitive?.expire || "—" : "** / **";
  const cvv = sensitiveRevealed ? cardSensitive?.cvv || "—" : "***";

  const cardInfoCard = (
    <section className={styles.cardInfoCard}>
      <div className={styles.cardInfoHeader}>
        <h3 className={styles.cardInfoTitle}>{t("spikexCard.cardInfoTitle")}</h3>
        <button
          type="button"
          className={styles.cardInfoEyeBtn}
          onClick={onToggleReveal}
          disabled={sensitiveLoading}
          aria-label={t(sensitiveRevealed ? "spikexCard.hideCardInfo" : "spikexCard.showCardInfo")}
        >
          {sensitiveLoading ? <Spin size="small" /> : <EyeIcon revealed={sensitiveRevealed} />}
        </button>
      </div>

      <div className={styles.cardInfoRow}>
        <span className={styles.cardInfoLabel}>{t("spikexCard.cardNumber")}</span>
        <div className={styles.cardInfoValueWrap}>
          <span className={styles.cardInfoValue}>{fullNumber}</span>
          {sensitiveRevealed && cardSensitive?.cardNumber ? (
            <CopyButton t={t} text={cardSensitive.cardNumber} />
          ) : null}
        </div>
      </div>
      <div className={styles.cardInfoDivider} />

      <div className={styles.cardInfoRow}>
        <span className={styles.cardInfoLabel}>{t("spikexCard.cardExpire")}</span>
        <div className={styles.cardInfoValueWrap}>
          <span className={styles.cardInfoValue}>{expire}</span>
          {sensitiveRevealed && cardSensitive?.expire ? <CopyButton t={t} text={cardSensitive.expire} /> : null}
        </div>
      </div>
      <div className={styles.cardInfoDivider} />

      <div className={styles.cardInfoRow}>
        <span className={styles.cardInfoLabel}>{t("spikexCard.cardCvv")}</span>
        <div className={styles.cardInfoValueWrap}>
          <span className={styles.cardInfoValue}>{cvv}</span>
          {sensitiveRevealed && cardSensitive?.cvv ? <CopyButton t={t} text={cardSensitive.cvv} /> : null}
        </div>
      </div>
    </section>
  );

  if (spikexPc) {
    return cardInfoCard;
  }

  return (
    <div className={styles.cardInfoWrap}>
      <button type="button" className={styles.cardInfoBackBtn} onClick={onBack}>
        {t("spikexCard.backToBalance")}
      </button>
      {cardInfoCard}
    </div>
  );
}

function CardInfoPcBack({ t, onBack }) {
  return (
    <button type="button" className={styles.cardInfoPcBack} onClick={onBack}>
      <svg className={styles.cardInfoPcBackIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {t("spikexCard.back")}
    </button>
  );
}

function FrozenStatusCard({ t, onContactSupport }) {
  return (
    <section className={styles.frozenStatusCard}>
      <div className={styles.frozenStatusMain}>
        <img className={styles.frozenStatusIcon} src={iconAttachmentRequired} alt="" />
        <div className={styles.frozenStatusText}>
          <div className={styles.frozenStatusTitle}>{t("spikexCard.statusFrozenTitle")}</div>
          <div className={styles.frozenStatusDesc}>{t("spikexCard.frozenUnavailableHint")}</div>
        </div>
      </div>
      <button type="button" className={styles.frozenContactBtn} onClick={onContactSupport}>
        {t("spikexCard.frozenContactBtn")}
      </button>
    </section>
  );
}

function FrozenPlaceholderPanel({ t }) {
  return (
    <section className={styles.frozenPlaceholder}>
      <p>{t("spikexCard.frozenUnavailableHint")}</p>
    </section>
  );
}

function FrozenManagePanel({ t, onContactSupport }) {
  return (
    <>
      <FrozenStatusCard t={t} onContactSupport={onContactSupport} />
      <FrozenPlaceholderPanel t={t} />
    </>
  );
}

export default function ManageCardContent({
  t,
  variant,
  cardBalance,
  cardInfo,
  displayBalance,
  balanceLoading,
  cardSensitive,
  onFetchSensitive,
  onClearSensitive,
  onRefreshBalance,
  onTopUp,
  onViewAllTransactions,
  onContactSupport,
  recentTransactions = [],
  recentTransactionsLoading = false,
  spikexPc = false,
}) {
  const [showCardInfo, setShowCardInfo] = useState(false);
  const [cardRulesOpen, setCardRulesOpen] = useState(false);
  const [sensitiveRevealed, setSensitiveRevealed] = useState(false);
  const [sensitiveLoading, setSensitiveLoading] = useState(false);

  const frozen = variant === "frozen";
  const cardNumber = cardBalance?.cardNumber;

  const handleToggleReveal = async () => {
    if (sensitiveRevealed) {
      setSensitiveRevealed(false);
      onClearSensitive?.();
      return;
    }

    setSensitiveLoading(true);
    try {
      const data = await onFetchSensitive();
      if (data) setSensitiveRevealed(true);
    } catch (error) {
      message.error(resolveHyperpayErrorMessage(t, error, "HYPERPAY_CARD_SENSITIVE_QUERY_FAILED"));
    } finally {
      setSensitiveLoading(false);
    }
  };

  const handleBackToBalance = () => {
    setShowCardInfo(false);
    setSensitiveRevealed(false);
    onClearSensitive?.();
  };

  const handleOpenCardInfo = () => {
    onRefreshBalance?.();
    setShowCardInfo(true);
  };

  const handleOpenRules = () => {
    setCardRulesOpen(true);
  };

  const handleViewAllTransactions = () => {
    onViewAllTransactions?.();
  };

  if (spikexPc) {
    return (
      <>
        <div className={cx(styles.manageContent, styles.manageContentPc)}>
        <div className={styles.managePcGrid}>
          <div className={styles.managePcLeftCol}>
            <ManagedCardVisual t={t} frozen={frozen} cardNumber={cardNumber} />
            {frozen ? (
              <FrozenStatusCard t={t} onContactSupport={onContactSupport} />
            ) : (
              <BalancePanel
                t={t}
                balance={displayBalance}
                cardCurrency={cardBalance?.cardCurrency}
                balanceLoading={balanceLoading}
                onOpenCardInfo={handleOpenCardInfo}
                onOpenRules={handleOpenRules}
                onDeposit={onTopUp}
              />
            )}
          </div>
          {frozen ? (
            <FrozenPlaceholderPanel t={t} />
          ) : showCardInfo ? (
            <div className={styles.managePcRightCol}>
              <CardInfoPcBack t={t} onBack={handleBackToBalance} />
              <CardInfoPanel
                t={t}
                cardBalance={cardBalance}
                cardSensitive={cardSensitive}
                sensitiveRevealed={sensitiveRevealed}
                sensitiveLoading={sensitiveLoading}
                onToggleReveal={handleToggleReveal}
                onBack={handleBackToBalance}
                spikexPc
              />
            </div>
          ) : (
            <RecentTransactionsPanel
              t={t}
              transactions={recentTransactions}
              loading={recentTransactionsLoading}
              onViewAll={handleViewAllTransactions}
            />
          )}
        </div>
        </div>
        <CardRulesModal
          t={t}
          open={cardRulesOpen}
          onClose={() => setCardRulesOpen(false)}
          cardInfo={cardInfo}
          spikexPc
        />
      </>
    );
  }

  return (
    <>
      <div className={cx(styles.manageContent, spikexPc && styles.manageContentPc)}>
      <ManagedCardVisual t={t} frozen={frozen} cardNumber={cardNumber} />
      {frozen ? (
        <FrozenManagePanel t={t} onContactSupport={onContactSupport} />
      ) : showCardInfo ? (
        <CardInfoPanel
          t={t}
          cardBalance={cardBalance}
          cardSensitive={cardSensitive}
          sensitiveRevealed={sensitiveRevealed}
          sensitiveLoading={sensitiveLoading}
          onToggleReveal={handleToggleReveal}
          onBack={handleBackToBalance}
        />
      ) : (
        <>
          <BalancePanel
            t={t}
            balance={displayBalance}
            cardCurrency={cardBalance?.cardCurrency}
            balanceLoading={balanceLoading}
            onOpenCardInfo={handleOpenCardInfo}
            onOpenRules={handleOpenRules}
            onDeposit={onTopUp}
          />
          <RecentTransactionsPanel
            t={t}
            transactions={recentTransactions}
            loading={recentTransactionsLoading}
            onViewAll={handleViewAllTransactions}
          />
        </>
      )}
      </div>
      <CardRulesModal
        t={t}
        open={cardRulesOpen}
        onClose={() => setCardRulesOpen(false)}
        cardInfo={cardInfo}
        spikexPc={spikexPc}
      />
    </>
  );
}
